/**
 * Colores de series para gráficos según marca blanca del gym/coach.
 * Prioriza tokens del shell (--shell-*) aplicados por branding.js.
 */

function parseHex(raw) {
  const s = String(raw || '').trim();
  const m = s.match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!m) return null;
  let h = m[1];
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  return h.toLowerCase();
}

function hexToRgb(hex) {
  const h = parseHex(hex);
  if (!h) return null;
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function rgbToHex({ r, g, b }) {
  const clamp = (n) => Math.max(0, Math.min(255, Math.round(n)));
  return `#${[clamp(r), clamp(g), clamp(b)].map((n) => n.toString(16).padStart(2, '0')).join('')}`;
}

function luminance(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0.5;
  return (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
}

/** Evita líneas casi negras sobre fondos oscuros del shell. */
function chartSafe(hex, fallback) {
  if (!parseHex(hex)) return fallback;
  if (luminance(hex) < 0.28) {
    return mixWith(hex, '#ffffff', 0.55);
  }
  return hex.startsWith('#') ? hex : `#${parseHex(hex)}`;
}

function mixWith(hexA, hexB, t = 0.5) {
  const a = hexToRgb(hexA);
  const b = hexToRgb(hexB);
  if (!a || !b) return hexA || hexB || '#ffd700';
  return rgbToHex({
    r: a.r + (b.r - a.r) * t,
    g: a.g + (b.g - a.g) * t,
    b: a.b + (b.b - a.b) * t,
  });
}

function shiftHue(hex, deg) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  const l = (max + min) / 2;
  const d = max - min;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  if (d !== 0) {
    switch (max) {
      case r: h = ((g - b) / d) % 6; break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4; break;
    }
    h *= 60;
    if (h < 0) h += 360;
  }
  h = (h + deg + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let rr = 0;
  let gg = 0;
  let bb = 0;
  if (h < 60) [rr, gg, bb] = [c, x, 0];
  else if (h < 120) [rr, gg, bb] = [x, c, 0];
  else if (h < 180) [rr, gg, bb] = [0, c, x];
  else if (h < 240) [rr, gg, bb] = [0, x, c];
  else if (h < 300) [rr, gg, bb] = [x, 0, c];
  else [rr, gg, bb] = [c, 0, x];
  return rgbToHex({ r: (rr + m) * 255, g: (gg + m) * 255, b: (bb + m) * 255 });
}

function pickCss(styles, names, fallback) {
  for (const name of names) {
    const v = styles.getPropertyValue(name).trim();
    if (v) return v;
  }
  return fallback;
}

/**
 * Paleta de 4 colores legibles en UI oscura, anclada a marca blanca.
 * @returns {{ a: string, b: string, c: string, d: string, primary: string }}
 */
export function resolveBrandChartColors() {
  const platform = '#ffd700';
  if (typeof window === 'undefined') {
    return {
      primary: platform,
      a: mixWith(platform, '#ffffff', 0.25),
      b: platform,
      c: shiftHue(platform, 140),
      d: shiftHue(platform, 40),
    };
  }
  const styles = getComputedStyle(document.documentElement);
  const primaryRaw = pickCss(styles, [
    '--shell-primary',
    '--brand-primary',
    '--shell-accent-readable',
    '--d28d-yellow',
    '--d28d-primary',
  ], platform);
  const accentRaw = pickCss(styles, [
    '--shell-accent-readable',
    '--d28d-yellow',
    '--shell-primary',
    '--brand-primary',
  ], primaryRaw);
  const secondaryRaw = pickCss(styles, [
    '--shell-secondary',
    '--brand-secondary',
    '--d28d-accent',
  ], '');

  const primary = chartSafe(primaryRaw, platform);
  const accent = chartSafe(accentRaw, primary);
  let secondary = secondaryRaw ? chartSafe(secondaryRaw, '') : '';
  if (!secondary || secondary.toLowerCase() === primary.toLowerCase()) {
    secondary = shiftHue(primary, 145);
  }
  const tertiary = mixWith(primary, '#ffffff', 0.35);
  const quaternary = shiftHue(primary, 55);

  return {
    primary,
    a: accent,
    b: primary,
    c: secondary,
    d: tertiary || quaternary,
  };
}

/** CSS var stack para bordes/toggles de gráficos (sin hardcode morado). */
export const BRAND_CHART_CSS_PRIMARY = 'var(--shell-primary, var(--brand-primary, var(--d28d-yellow, #ffd700)))';
